import { useState } from "react";
import {
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Clock,
  FolderKanban,
  Bookmark,
  BookmarkCheck,
  Trash2,
  Check,
  Newspaper,
  Sparkles,
  Share2,
  Link2,
  Mail,
  MessageCircle,
  Linkedin,
  Facebook,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ScoreBadge } from "./ScoreBadge";
import { api } from "../lib/api";
import { fmtDate, sentimentColor, templateMeta, isLinkedInItem } from "../lib/format";
import AskResultPopover from "./AskResultPopover";

export default function ResultCard({
  item,
  project,
  onOpen,
  onFeedback,
  isBookmarked,
  onToggleBookmark,
  isRead = false,
  // P6 — soft-delete / multi-select
  onHide,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
  showCrossProject = true,
}) {
  const [fb, setFb] = useState(item.feedback);
  const [cpExpanded, setCpExpanded] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [resolvingShare, setResolvingShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const tm = templateMeta(item.template_id || "news_monitoring");
  const isLinkedIn = isLinkedInItem(item);
  const typeLabel = isLinkedIn ? "LinkedIn Article" : tm.label;
  const typeColor = isLinkedIn ? "#0A66C2" : tm.color;

  const resolvedSourceUrl = Array.isArray(item.duplicate_sources)
    ? item.duplicate_sources.map((s) => s.url).find((u) => u && !u.includes("news.google.com"))
    : null;
  // Prefer resolved non-google URL; fallback to original only if no resolved available
  const effectiveShareUrl = (shareUrl && !shareUrl.includes("news.google.com") ? shareUrl : null) || resolvedSourceUrl || (item.url && !item.url.includes("news.google.com") ? item.url : null) || shareUrl || item.url || window.location.href;
  const sources = Array.isArray(item.duplicate_sources) && item.duplicate_sources.length > 0
    ? item.duplicate_sources.map((s) => s.source || s.url).filter(Boolean)
    : [item.source].filter(Boolean);
  const shareText = [item.title, "", item.summary, "", `Sources: ${sources.join(", ") || "Original article"}`, effectiveShareUrl].filter(Boolean).join("\n");
  const shareChannels = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareText)}` },
    { label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(shareText)}` },
    { label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(effectiveShareUrl)}` },
    { label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(effectiveShareUrl)}` },
  ];
  const openShare = async (e) => {
    e.stopPropagation();
    if (shareOpen) { setShareOpen(false); return; }
    const isGoogle = item.url?.includes("news.google.com");
    const isShareUrlResolved = shareUrl && !shareUrl.includes("news.google.com");
    if (!isGoogle || isShareUrlResolved || !item.id) { setShareOpen(true); return; }
    setResolvingShare(true);
    try {
      const r = await api.get(`/results/${item.id}/share-url`);
      if (r.data?.url) setShareUrl(r.data.url);
      setShareOpen(true);
    } finally { setResolvingShare(false); }
  };
  const copyShareLink = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(shareText); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { setCopied(false); }
  };

  const handleFb = async (e, val) => {
    e.stopPropagation();

    const next = fb === val ? "clear" : val;
    setFb(next === "clear" ? null : next);

    try {
      await api.post(`/results/${item.id}/feedback`, {
        feedback: next,
      });

      onFeedback?.();
    } catch (err) {
      console.error("ResultCard feedback failed", err);
    }
  };

  const handleBookmark = (e) => {
    e.stopPropagation();
    onToggleBookmark?.(item);
  };

  const handleHide = (e) => {
    e.stopPropagation();
    onHide?.(item);
  };

  const handleSelectToggle = (e) => {
    e.stopPropagation();
    onToggleSelect?.(item);
  };

  const handleCardClick = () => {
    if (selectMode) {
      onToggleSelect?.(item);
    } else {
      onOpen(item);
    }
  };

  return (
    <div className="relative group/card">
      <div
        data-testid={`result-card-${item.id}`}
        data-read={isRead ? "true" : "false"}
        data-selected={isSelected ? "true" : "false"}
        onClick={handleCardClick}
        className={`
          relative bg-white border rounded-lg p-6 flex gap-5 cursor-pointer
          hover:border-[#0F1F3D] hover:-translate-y-0.5 hover:shadow-lg
          transition-all duration-200
          ${isSelected
            ? "border-[#0F1F3D] ring-2 ring-[#0F1F3D]/15 bg-[#F8FAFC]"
            : (isRead
              ? "border-[#E2E8F0] bg-[#F8FAFC] opacity-[0.78]"
              : "border-[#E2E8F0]")}
        `}
      >
        {selectMode && (
          <button
            type="button"
            onClick={handleSelectToggle}
            aria-label={isSelected ? "Deselect" : "Select"}
            data-testid={`select-toggle-${item.id}`}
            className={`absolute top-4 left-4 h-5 w-5 rounded border flex items-center justify-center transition-colors ${isSelected
                ? "bg-[#0F1F3D] border-[#0F1F3D] text-white"
                : "bg-white border-[#CBD5E1] hover:border-[#0F1F3D]"
              }`}
          >
            {isSelected && <Check className="h-3.5 w-3.5" />}
          </button>
        )}

        <ScoreBadge score={item.relevance_score} size="lg" />


        <div className="absolute top-4 right-4 flex items-center gap-1">
          <span className="inline-flex items-center gap-1 text-[#94A3B8] type-caption">
              {/* <Clock className="h-3 w-3" /> */}
              {new Date(item.run_date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          <div className="relative">
            <button
              type="button"
              onClick={openShare}
              aria-label="Share"
              data-testid={`share-toggle-${item.id}`}
              title="Share"
              disabled={resolvingShare}
              className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#0F1F3D] hover:bg-[#F1F5F9] transition-colors disabled:opacity-50 disabled:cursor-wait"
            >
              {resolvingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            </button>
            {shareOpen && (
              <div className="absolute right-0 top-full mt-2 z-20 w-52 rounded-md border border-[#E2E8F0] bg-white p-1.5 shadow-xl" data-testid={`share-menu-${item.id}`} onClick={(e) => e.stopPropagation()}>
                {shareChannels.map(({ label, icon: Icon, href }) => (
                  <a key={label} href={href} target={label === "Email" ? undefined : "_blank"} rel={label === "Email" ? undefined : "noreferrer"} onClick={() => setShareOpen(false)} className="flex items-center gap-2 rounded px-2.5 py-2 text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]">
                    <Icon className="h-4 w-4 text-[#64748B]" /> {label}
                  </a>
                ))}
                <button type="button" onClick={copyShareLink} disabled={resolvingShare} className="flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-sm text-[#0F1F3D] hover:bg-[#F1F5F9] disabled:opacity-50">
                  <Link2 className="h-4 w-4 text-[#64748B]" /> {copied ? "Copied" : "Copy link"}
                </button>
                {resolvingShare && <div className="px-2.5 py-1 text-xs text-[#64748B] inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Resolving link...</div>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleHide}
            aria-label="Delete from my feed"
            data-testid={`delete-toggle-${item.id}`}
            title="Delete from my feed"
            className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleBookmark}
            aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            data-testid={`bookmark-toggle-${item.id}`}
            className={`p-1.5 rounded-md transition-colors ${isBookmarked
              ? "text-[#0F1F3D] hover:bg-[#F1F5F9]"
              : "text-[#94A3B8] hover:text-[#0F1F3D] hover:bg-[#F1F5F9]"
              }`}
          >
            {isBookmarked ? (
              <BookmarkCheck className="h-4 w-4" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className={`flex-1 min-w-0 ${selectMode ? "pl-6" : ""}`}>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {project?.name && (
              <span
                data-testid={`result-project-${item.id}`}
                className="
                  inline-flex items-center gap-1 type-eyebrow
                  text-[#0F1F3D] bg-[#F1F5F9]
                  px-2 py-0.5 rounded-md
                "
              >
                <FolderKanban className="h-3 w-3" />
                {project.name}
              </span>
            )}

            <span
              className="px-2 py-0.5 rounded-md type-eyebrow"
              style={{
                background: typeColor + "15",
                color: typeColor,
              }}
            >
              {typeLabel}
            </span>

            {isRead && (
              <span
                data-testid={`result-read-badge-${item.id}`}
                className="px-2 py-0.5 rounded-md type-eyebrow bg-[#E2E8F0] text-[#64748B]"
              >
                Read
              </span>
            )}
          </div>

          <h3
            className={`type-h4 ${isRead ? "text-[#64748B] font-normal" : "text-[#0F1F3D]"}`}
            data-testid={`result-title-${item.id}`}
          >
            {item.title}
          </h3>

          <p className="mt-2 type-body text-[#475569] line-clamp-2 pr-20">
            {item.summary}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2 type-caption">
            <span className="text-[#64748B]">{item.source}</span>
            <span className="text-[#CBD5E1]">·</span>
            <span className="text-[#64748B]">
              {fmtDate(item.published_at)}
            </span>

            {item.outlet_count > 1 && (
              <>
                <span className="text-[#CBD5E1]">·</span>
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#3730A3] type-eyebrow font-semibold"
                  data-testid={`result-outlet-count-${item.id}`}
                  title="This story was covered by multiple sources"
                >
                  <Newspaper className="h-3 w-3" />
                  {item.outlet_count} sources
                </span>
              </>
            )}

            {item.category && (
              <>
                <span className="text-[#CBD5E1]">·</span>
                <span className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569] type-eyebrow">
                  {item.category}
                </span>
              </>
            )}

            {item.sentiment && item.sentiment !== "neutral" && (
              <span
                className="px-2 py-0.5 rounded-full type-eyebrow"
                style={{
                  background: sentimentColor(item.sentiment) + "1A",
                  color: sentimentColor(item.sentiment),
                }}
              >
                {item.sentiment}
              </span>
            )}



          </div>

          <div className="mt-4 flex items-center gap-2">
            <div className="relative group/tooltip inline-flex">

              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`result-open-link-${item.id}`}
                className="
      inline-flex
      items-center
      gap-1
      type-caption
      font-semibold
      text-[#0F1F3D]
      hover:underline
    "
              >
                <ExternalLink className="h-3 w-3" />
                Read article
              </a>

              <div
                className="
      absolute
      bottom-full
      left-1/2
      mb-2
      -translate-x-1/2

      rounded-lg
      bg-[#0F172A]

      px-3
      py-2

      text-[11px]
      font-medium
      text-white

      whitespace-nowrap

      opacity-0
      invisible
      translate-y-1

      transition-all
      duration-150

      group-hover/tooltip:opacity-100
      group-hover/tooltip:visible
      group-hover/tooltip:translate-y-0

      pointer-events-none
      z-50
    "
              >
                Open original source
                <div
                  className="
        absolute
        left-1/2
        top-full
        h-2
        w-2

        -translate-x-1/2
        -translate-y-1

        rotate-45
        bg-[#0F172A]
      "
                />
              </div>

            </div>

            <div
              className="ml-auto flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mr-2">
                <span className="ml-auto inline-flex items-center gap-1 text-[#94A3B8] type-caption">
                  <Clock className="h-3 w-3" />
                  {item.estimated_read_time}m
                </span>
              </div>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setAskOpen(true); }}
                data-testid={`result-ask-ai-${item.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#0F1F3D] text-white text-xs font-semibold hover:bg-[#172A4F] transition-colors"
                title="Ask AI about this article"
              >
                <Sparkles className="h-3 w-3" /> Ask AI
              </button>
              <div className="relative group/tooltip">
                <button
                  type="button"
                  onClick={(e) => handleFb(e, "up")}
                  data-testid={`thumbs-up-${item.id}`}
                  className={`p-1.5 rounded-md border transition-colors ${fb === "up"
                    ? "bg-[#10B981] border-[#10B981] text-white"
                    : "border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]"
                    }`}
                >
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>

                <div
                  className="
                    absolute bottom-full left-1/2 mb-2 -translate-x-1/2
                    rounded-lg bg-[#0F172A] px-3 py-2
                    text-[11px] font-medium text-white whitespace-nowrap
                    opacity-0 invisible translate-y-1
                    transition-all duration-150
                    group-hover/tooltip:opacity-100
                    group-hover/tooltip:visible
                    group-hover/tooltip:translate-y-0
                    pointer-events-none z-50
                  "
                >
                  Mark as relevant

                  <div
                    className="
                      absolute left-1/2 top-full h-2 w-2
                      -translate-x-1/2 -translate-y-1 rotate-45
                      bg-[#0F172A]
                    "
                  />
                </div>
              </div>

              <div className="relative group/tooltip">
                <button
                  type="button"
                  onClick={(e) => handleFb(e, "down")}
                  data-testid={`thumbs-down-${item.id}`}
                  className={`p-1.5 rounded-md border transition-colors ${fb === "down"
                    ? "bg-[#EF4444] border-[#EF4444] text-white"
                    : "border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9]"
                    }`}
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>

                <div
                  className="
                    absolute bottom-full left-1/2 mb-2 -translate-x-1/2
                    rounded-lg bg-[#0F172A] px-3 py-2
                    text-[11px] font-medium text-white whitespace-nowrap
                    opacity-0 invisible translate-y-1
                    transition-all duration-150
                    group-hover/tooltip:opacity-100
                    group-hover/tooltip:visible
                    group-hover/tooltip:translate-y-0
                    pointer-events-none z-50
                  "
                >
                  Mark as not relevant

                  <div
                    className="
                      absolute left-1/2 top-full h-2 w-2
                      -translate-x-1/2 -translate-y-1 rotate-45
                      bg-[#0F172A]
                    "
                  />
                </div>
              </div>


            </div>
          </div>

          {showCrossProject && item.cross_project_matches && item.cross_project_matches.length > 0 && (
            <div
              className="mt-3.5 rounded-lg border border-[#DDD6FE] bg-[#FAF5FF]/60 p-3 text-left transition-all"
              data-testid={`cross-project-card-box-${item.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header Row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 rounded-full bg-[#EDE9FE] flex items-center justify-center shrink-0">
                    <Sparkles className="h-3 w-3 text-[#7C3AED]" />
                  </div>
                  <span className="text-xs font-semibold text-[#0F1F3D]">Relevant to other projects</span>
                  <div className="relative group/cptip inline-flex">
                    <Info className="h-3.5 w-3.5 text-[#94A3B8] hover:text-[#0F1F3D] cursor-help" />
                    <div
                      className="
                        absolute bottom-full left-1/2 mb-2 -translate-x-1/2
                        rounded-lg bg-[#0F172A] px-2.5 py-1.5
                        text-[11px] font-medium text-white whitespace-nowrap
                        opacity-0 invisible translate-y-1
                        transition-all duration-150
                        group-hover/cptip:opacity-100
                        group-hover/cptip:visible
                        group-hover/cptip:translate-y-0
                        pointer-events-none z-50 shadow-md
                      "
                    >
                      Other projects in your organisation that may be impacted
                      <div className="absolute left-1/2 top-full h-1.5 w-1.5 -translate-x-1/2 -translate-y-0.5 rotate-45 bg-[#0F172A]" />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCpExpanded(!cpExpanded);
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-[#64748B] hover:text-[#0F1F3D] px-1.5 py-0.5 rounded hover:bg-[#EDE9FE]/50 transition-colors"
                  data-testid={`cross-project-card-toggle-${item.id}`}
                >
                  <span>
                    {item.cross_project_matches.length} related project{item.cross_project_matches.length > 1 ? "s" : ""}
                  </span>
                  {cpExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
              </div>

              {/* Cards Row (when expanded) */}
              {cpExpanded && (
                <div className="mt-2.5 flex flex-wrap gap-2.5">
                  {item.cross_project_matches.map((match) => {
                    const conf = (match.confidence_level || "medium").toLowerCase();
                    const style =
                      conf === "high"
                        ? {
                            border: "border-emerald-200",
                            text: "text-emerald-700",
                            dot: "bg-emerald-500",
                            label: "High relevance",
                          }
                        : conf === "low"
                        ? {
                            border: "border-purple-200",
                            text: "text-purple-700",
                            dot: "bg-purple-500",
                            label: "Low relevance",
                          }
                        : {
                            border: "border-blue-200",
                            text: "text-blue-700",
                            dot: "bg-blue-500",
                            label: "Medium relevance",
                          };

                    return (
                      <div
                        key={match.project_id}
                        title={match.reasoning || undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/feed?project_id=${match.project_id}`);
                        }}
                        className={`
                          group/item flex-1 min-w-[140px] sm:min-w-[170px] max-w-[240px]
                          rounded-lg border bg-white p-2.5 shadow-2xs hover:shadow-xs
                          hover:border-[#0F1F3D] transition-all cursor-pointer
                          ${style.border}
                        `}
                        data-testid={`cross-project-match-${match.project_id}`}
                      >
                        <div className="text-xs font-semibold text-[#0F1F3D] truncate group-hover/item:text-[#172A4F]">
                          {match.project_name}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${style.dot}`} />
                          <span className={`text-[11px] font-medium ${style.text}`}>
                            {style.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AskResultPopover
        open={askOpen}
        onClose={() => setAskOpen(false)}
        projectName={project?.name}
        resultTitle={item.title}
        onSubmit={(q) => {
          setAskOpen(false);
          const params = new URLSearchParams({ q, project: item.project_id || "", resultId: item.id, new: "1" });
          navigate(`/app/ai-analysis?${params.toString()}`);
        }}
      />

      {/* Card Tooltip - hidden when Ask AI popover is open */}
      {!askOpen && (
        <div
          className="
            absolute bottom-full left-6 mb-3 z-50 w-[260px]
            rounded-xl bg-[#0F172A] px-4 py-3 shadow-2xl
            opacity-0 invisible translate-y-2
            transition-all duration-200
            group-hover/card:opacity-100
            group-hover/card:visible
            group-hover/card:translate-y-0
            pointer-events-none
          "
        >
        <div className="text-[13px] font-semibold text-white">
          Match Score
        </div>

        <p className="mt-1 text-[12px] leading-relaxed text-slate-300">
          Score out of 100. Shows how strongly this result matches your project
          requirements.
        </p>

        <div
          className="
            absolute left-6 top-full h-3 w-3
            rotate-45 bg-[#0F172A] -translate-y-1.5
          "
        />
        </div>
      )}
    </div>
  );
}